/**
 * Skills Catalog page.
 *
 * Sprint 131 Stream C — M-A0 catalog grid.
 * Lists all skills accessible to the platform admin (platform_skill,
 * platform_official_skill, plus any tenant_skill / byos_skill via the
 * admin endpoint when scoped by tenant_id).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { unwrapImportedField } from '@/lib/importedField';
import { useTenant } from '@/contexts/TenantContext';
import { HelpLink, HELP_ARTICLES } from '@vanguard-atlas/shared';
import {
  Badge,
  Button,
  Card,
  Spinner,
  StatusBadge,
} from '@/components/ui';
import { Plus, Sparkles, Tag, Upload } from 'lucide-react';
import { useAdminSkills, useAdminSkillFacets } from '@/features/skills';
import type {
  SkillLifecycleStatus,
  SkillSummary,
  SkillTier,
} from '@/features/skills';
import { UnifiedSkillSearchInput } from '@/features/skills/components/UnifiedSkillSearchInput';
import { parseUnifiedSkillSearch } from '@/features/skills/utils/unifiedSearch';
import { SkillCreateBYOSModal } from './modals/SkillCreateBYOSModal';
import { SkillImportWizard } from './modals/SkillImportWizard';

const TIER_OPTIONS: { value: SkillTier | ''; labelKey: string }[] = [
  { value: '', labelKey: 'admin.skills.catalog.tierAll' },
  { value: 'platform_skill', labelKey: 'admin.skills.catalog.tierPlatform' },
  { value: 'platform_official_skill', labelKey: 'admin.skills.catalog.tierPlatformOfficial' },
  { value: 'tenant_skill', labelKey: 'admin.skills.catalog.tierTenant' },
  { value: 'byos_skill', labelKey: 'admin.skills.catalog.tierByos' },
];

const TIER_LABEL_KEYS: Record<SkillTier, string> = {
  platform_skill: 'admin.skills.catalog.tierPlatform',
  platform_official_skill: 'admin.skills.catalog.tierPlatformOfficial',
  tenant_skill: 'admin.skills.catalog.tierTenant',
  byos_skill: 'admin.skills.catalog.tierByos',
};

const LIFECYCLE_OPTIONS: { value: SkillLifecycleStatus | ''; labelKey: string }[] = [
  { value: '', labelKey: 'admin.skills.catalog.statusAll' },
  { value: 'draft', labelKey: 'admin.skills.catalog.statusDraft' },
  { value: 'preview', labelKey: 'admin.skills.catalog.statusPreview' },
  { value: 'published', labelKey: 'admin.skills.catalog.statusPublished' },
  { value: 'deprecated', labelKey: 'admin.skills.catalog.statusDeprecated' },
  { value: 'archived', labelKey: 'admin.skills.catalog.statusArchived' },
];

const LIFECYCLE_LABEL_KEYS: Record<SkillLifecycleStatus, string> = {
  draft: 'admin.skills.catalog.statusDraft',
  preview: 'admin.skills.catalog.statusPreview',
  published: 'admin.skills.catalog.statusPublished',
  deprecated: 'admin.skills.catalog.statusDeprecated',
  archived: 'admin.skills.catalog.statusArchived',
};

function tierBadgeVariant(tier: SkillTier): 'default' | 'secondary' | 'outline' {
  switch (tier) {
    case 'platform_official_skill':
      return 'default';
    case 'platform_skill':
      return 'secondary';
    default:
      return 'outline';
  }
}

function lifecycleStatusType(
  status: SkillLifecycleStatus,
): 'active' | 'draft' | 'warning' | 'error' | 'deprecated' | 'default' {
  switch (status) {
    case 'published':
      return 'active';
    case 'preview':
      return 'warning';
    case 'draft':
      return 'draft';
    case 'deprecated':
      return 'deprecated';
    case 'archived':
      return 'error';
    default:
      return 'default';
  }
}

export function SkillsCatalogPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();

  const [query, setQuery] = useState('');
  const [tier, setTier] = useState<SkillTier | ''>('');
  const [lifecycle, setLifecycle] = useState<SkillLifecycleStatus | ''>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Scope by the current tenant so tenant-tier (BYOS) skills are visible and
  // searchable in the grid (ISS-255). Without a tenant, only platform rows
  // are returned by the backend — previous behavior.
  const { data: facets } = useAdminSkillFacets(currentTenant?.id);

  const parsed = useMemo(() => parseUnifiedSkillSearch(query), [query]);

  const { data, isLoading, error } = useAdminSkills({
    tenant_id: currentTenant?.id,
    search: parsed.search,
    tier: tier || undefined,
    lifecycle: lifecycle || undefined,
    vertical: parsed.vertical,
    function: parsed.functionName,
    tags_all: parsed.tagsAll.length ? parsed.tagsAll : undefined,
    tags_none: parsed.tagsNone.length ? parsed.tagsNone : undefined,
    page,
    page_size: 30,
  });

  // api.get unwraps response.data.data; align with MCP list page heuristic.
  const skills: SkillSummary[] = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : data.data ?? [];
  }, [data]);
  const total =
    data && !Array.isArray(data) ? data.total ?? skills.length : skills.length;
  const pageSize = 30;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            {t('admin.skills.catalog.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.skills.catalog.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpLink
            slug={HELP_ARTICLES.SKILLS_ACTIVATION}
            label={t('common.help')}
            ariaLabel={`${t('common.help')}: ${t('common.helpOpensNewTab')}`}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1"
          />
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="skills-import-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('admin.skills.catalog.importSkill')}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.skills.catalog.createByos')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <UnifiedSkillSearchInput
            value={query}
            onChange={(next) => {
              setQuery(next);
              setPage(1);
            }}
            facets={{
              verticals: facets?.verticals ?? [],
              functions: facets?.functions ?? [],
              tags: facets?.tags ?? [],
            }}
            placeholder={t('admin.skills.catalog.searchPlaceholder')}
            className="lg:col-span-2 md:col-span-3"
          />
          <select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value as SkillTier | '');
              setPage(1);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {TIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
          <select
            value={lifecycle}
            onChange={(e) => {
              setLifecycle(e.target.value as SkillLifecycleStatus | '');
              setPage(1);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {LIFECYCLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Body */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Card className="p-6 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">
            {t('admin.skills.catalog.loadError', { message: (error as Error).message })}
          </p>
        </Card>
      ) : skills.length === 0 ? (
        <Card className="p-12 text-center">
          <Sparkles className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-700 mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('admin.skills.catalog.emptyTitle')}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {query || tier || lifecycle
              ? t('admin.skills.catalog.emptyFilters')
              : t('admin.skills.catalog.emptyFirst')}
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {skills.map((skill) => (
              <button
                key={skill.id}
                type="button"
                onClick={() => navigate(`/skills/${encodeURIComponent(skill.slug)}`)}
                className="text-left"
              >
                <Card className="p-4 h-full hover:border-primary transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate">
                        {skill.slug}
                      </span>
                    </div>
                    <Badge variant={tierBadgeVariant(skill.tier)} className="ml-2">
                      {t(TIER_LABEL_KEYS[skill.tier])}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-1">
                    {skill.display_name}
                  </h3>
                  {skill.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                      {unwrapImportedField(skill.description)}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge variant="outline" className="text-xs">
                      {skill.vertical}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {skill.function}
                    </Badge>
                    {skill.is_beta && (
                      <Badge variant="secondary" className="text-xs">
                        {t('admin.skills.catalog.badgeBeta')}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                      {skill.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1"
                        >
                          <Tag className="h-3 w-3" />
                          {tag}
                        </span>
                      ))}
                      {skill.tags.length > 3 && (
                        <span>+{skill.tags.length - 3}</span>
                      )}
                    </div>
                    <StatusBadge
                      status={lifecycleStatusType(skill.lifecycle_status)}
                      label={t(LIFECYCLE_LABEL_KEYS[skill.lifecycle_status])}
                    />
                  </div>
                </Card>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('admin.skills.catalog.showing', { shown: skills.length, total })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {t('admin.skills.catalog.previous')}
                </Button>
                <span className="text-sm">
                  {t('admin.skills.catalog.pageOf', { page, pages: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  {t('admin.skills.catalog.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <SkillCreateBYOSModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(slug) => {
          setCreateOpen(false);
          navigate(`/skills/${encodeURIComponent(slug)}`);
        }}
      />
      <SkillImportWizard
        open={importOpen}
        onOpenChange={setImportOpen}
      />
    </div>
  );
}

export default SkillsCatalogPage;
